import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, Users, Zap, Target, Brain, TrendingUp, Shield, 
  MessageSquare, Rocket, Compass, Star, Loader2, Download,
  CheckCircle, AlertTriangle, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DESIGNATIONS = [
  { value: 'cxo', label: 'CEO / CXO' },
  { value: 'senior_leadership', label: 'Senior Leadership (VP/Director)' },
  { value: 'middle_management', label: 'Middle Management (Manager/Lead)' },
  { value: 'individual_contributor', label: 'Individual Contributor' },
  { value: 'entry_level', label: 'Entry Level / Fresh Talent' },
];

const COMPATIBILITY_PARAMETERS = [
  { id: 'cultural_alignment', name: 'Cultural Alignment', icon: Users, description: 'Shared values, beliefs, and organizational culture fit' },
  { id: 'leadership_style', name: 'Leadership Style', icon: Target, description: 'Approach to leading, delegating, and team management' },
  { id: 'decision_speed', name: 'Decision Speed', icon: Zap, description: 'Pace of decision-making and urgency orientation' },
  { id: 'innovation_orientation', name: 'Innovation Orientation', icon: Rocket, description: 'Openness to new ideas, change, and creative thinking' },
  { id: 'risk_tolerance', name: 'Risk Tolerance', icon: Shield, description: 'Comfort with uncertainty and risk-taking behavior' },
  { id: 'communication_style', name: 'Communication Style', icon: MessageSquare, description: 'Preferred modes and patterns of communication' },
  { id: 'execution_rhythm', name: 'Execution Rhythm', icon: TrendingUp, description: 'Work pace, deadline orientation, and delivery style' },
  { id: 'strategic_thinking', name: 'Strategic Thinking', icon: Brain, description: 'Long-term vision and strategic planning alignment' },
  { id: 'interpersonal_dynamics', name: 'Interpersonal Dynamics', icon: Compass, description: 'Social interaction patterns and relationship building' },
  { id: 'cosmic_harmony', name: 'Deep Compatibility', icon: Star, description: 'Temporal and behavioral compatibility factors' },
];

const getCompatibilityColor = (score) => {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-[#C5A065]';
  if (score >= 40) return 'text-yellow-400';
  return 'text-red-400';
};

const getCompatibilityBg = (score) => {
  if (score >= 80) return 'from-green-500/20 to-green-500/5';
  if (score >= 60) return 'from-[#C5A065]/20 to-[#C5A065]/5';
  if (score >= 40) return 'from-yellow-500/20 to-yellow-500/5';
  return 'from-red-500/20 to-red-500/5';
};

const getCompatibilityLabel = (score) => {
  if (score >= 85) return 'Excellent Match';
  if (score >= 70) return 'Good Match';
  if (score >= 55) return 'Moderate Match';
  if (score >= 40) return 'Fair Match';
  return 'Needs Attention';
};

export const CompatibilityReportPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [compatibilityResult, setCompatibilityResult] = useState(null);
  
  // Selection state
  const [person1, setPerson1] = useState({ reportId: '', designation: '' });
  const [person2, setPerson2] = useState({ reportId: '', designation: '' });

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      navigate('/');
      return;
    }
    if (user?.is_admin) {
      fetchReports();
    }
  }, [user, authLoading, navigate]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports?per_page=100`);
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const generateCompatibilityReport = async () => {
    if (!person1.reportId || !person1.designation || !person2.reportId || !person2.designation) {
      toast.error('Please select both persons and their designations');
      return;
    }

    if (person1.reportId === person2.reportId) {
      toast.error('Please select two different reports');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/compatibility/generate`, {
        report_id_1: person1.reportId,
        designation_1: person1.designation,
        report_id_2: person2.reportId,
        designation_2: person2.designation,
      });
      setCompatibilityResult(response.data);
      toast.success('Compatibility report generated!');
    } catch (error) {
      toast.error('Failed to generate compatibility report');
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async () => {
    if (!compatibilityResult?.id) return;
    try {
      const response = await axios.get(`${API}/admin/compatibility/download/${compatibilityResult.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Compatibility_Report_${compatibilityResult.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#CC5500] animate-spin" />
      </div>
    );
  }

  const getReportLabel = (report) => {
    return `${report.user_name} (${report.user_type === 'student' ? 'Student' : 'Professional'})`;
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="compatibility-report-page">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#CC5500]/10 rounded-full filter blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C5A065]/10 rounded-full filter blur-[100px]" />
      </div>

      {/* Header */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Admin
              </Button>
            </div>
            <h1 className="text-xl font-heading font-semibold text-white">
              Lead<span className="text-[#CC5500]">Economics</span>™
            </h1>
            <div className="w-32" />
          </div>
        </div>
      </nav>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#CC5500]/20 border border-[#CC5500]/30 mb-4">
            <Sparkles className="h-4 w-4 text-[#CC5500]" />
            <span className="text-sm font-medium text-[#CC5500]">360° Compatibility Analysis</span>
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-4">
            Leadership Compatibility Report
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Compare two leadership profiles across 10 critical dimensions to determine organizational fit and synergy potential.
          </p>
        </div>

        {!compatibilityResult ? (
          <>
            {/* Selection Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Person 1 */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#CC5500] flex items-center justify-center text-white text-sm font-bold">1</div>
                  First Person
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Select Report</label>
                    <Select value={person1.reportId} onValueChange={(v) => setPerson1({ ...person1, reportId: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Choose a report..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        {reports.map((report) => (
                          <SelectItem key={report.id} value={report.id} className="text-white hover:bg-white/10">
                            {getReportLabel(report)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Designation</label>
                    <Select value={person1.designation} onValueChange={(v) => setPerson1({ ...person1, designation: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select designation..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        {DESIGNATIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value} className="text-white hover:bg-white/10">
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Person 2 */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#C5A065] flex items-center justify-center text-white text-sm font-bold">2</div>
                  Second Person
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Select Report</label>
                    <Select value={person2.reportId} onValueChange={(v) => setPerson2({ ...person2, reportId: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Choose a report..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        {reports.filter(r => r.id !== person1.reportId).map((report) => (
                          <SelectItem key={report.id} value={report.id} className="text-white hover:bg-white/10">
                            {getReportLabel(report)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Designation</label>
                    <Select value={person2.designation} onValueChange={(v) => setPerson2({ ...person2, designation: v })}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select designation..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        {DESIGNATIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value} className="text-white hover:bg-white/10">
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <div className="text-center">
              <Button
                onClick={generateCompatibilityReport}
                disabled={generating || !person1.reportId || !person2.reportId || !person1.designation || !person2.designation}
                className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90 text-white px-12 py-6 text-lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Generating Compatibility Report...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate Compatibility Report
                  </>
                )}
              </Button>
            </div>

            {/* Parameters Preview */}
            <div className="mt-16">
              <h3 className="text-xl font-semibold text-white mb-6 text-center">10 Compatibility Parameters</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {COMPATIBILITY_PARAMETERS.map((param) => (
                  <div key={param.id} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                    <param.icon className="h-6 w-6 text-[#CC5500] mx-auto mb-2" />
                    <p className="text-sm text-white font-medium">{param.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Compatibility Results */
          <div className="space-y-8">
            {/* Overall Score */}
            <div className="bg-gradient-to-br from-[#CC5500]/20 to-[#C5A065]/10 border border-[#CC5500]/30 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-semibold text-white mb-2">Overall Compatibility</h2>
              <p className="text-gray-400 mb-6">
                {compatibilityResult.person1_name} ({DESIGNATIONS.find(d => d.value === person1.designation)?.label}) 
                <span className="mx-2 text-[#CC5500]">↔</span>
                {compatibilityResult.person2_name} ({DESIGNATIONS.find(d => d.value === person2.designation)?.label})
              </p>
              
              <div className="relative w-40 h-40 mx-auto mb-4">
                <svg className="w-40 h-40 transform -rotate-90">
                  <circle cx="80" cy="80" r="70" fill="none" stroke="#1a1a1a" strokeWidth="12" />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    fill="none"
                    stroke="url(#compatGradient)"
                    strokeWidth="12"
                    strokeDasharray={`${2 * Math.PI * 70}`}
                    strokeDashoffset={`${2 * Math.PI * 70 * (1 - compatibilityResult.overall_score / 100)}`}
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="compatGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#CC5500" />
                      <stop offset="100%" stopColor="#C5A065" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-5xl font-bold text-white">{compatibilityResult.overall_score}</span>
                  <span className="text-sm text-gray-400">out of 100</span>
                </div>
              </div>
              
              <p className={`text-xl font-semibold ${getCompatibilityColor(compatibilityResult.overall_score)}`}>
                {getCompatibilityLabel(compatibilityResult.overall_score)}
              </p>
            </div>

            {/* Parameter Scores */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-6">Detailed Parameter Analysis</h3>
              
              {COMPATIBILITY_PARAMETERS.map((param) => {
                const score = compatibilityResult.parameters[param.id] || 0;
                const analysis = compatibilityResult.analysis?.[param.id] || '';
                
                return (
                  <div key={param.id} className={`bg-gradient-to-r ${getCompatibilityBg(score)} border border-white/10 rounded-xl p-6`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                        <param.icon className={`h-6 w-6 ${getCompatibilityColor(score)}`} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-lg font-semibold text-white">{param.name}</h4>
                          <div className="flex items-center gap-2">
                            <span className={`text-2xl font-bold ${getCompatibilityColor(score)}`}>{score}%</span>
                            {score >= 70 ? (
                              <CheckCircle className="h-5 w-5 text-green-400" />
                            ) : score >= 50 ? (
                              <AlertTriangle className="h-5 w-5 text-yellow-400" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-red-400" />
                            )}
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-3">{param.description}</p>
                        
                        {/* Progress bar */}
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
                          <div 
                            className="h-full bg-gradient-to-r from-[#CC5500] to-[#C5A065] rounded-full transition-all duration-1000"
                            style={{ width: `${score}%` }}
                          />
                        </div>
                        
                        {/* Analysis text */}
                        {analysis && (
                          <p className="text-sm text-gray-400 italic">"{analysis}"</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            {compatibilityResult.summary && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Star className="h-5 w-5 text-[#C5A065]" />
                  Compatibility Summary
                </h3>
                <p className="text-gray-400 leading-relaxed">{compatibilityResult.summary}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => setCompatibilityResult(null)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                New Comparison
              </Button>
              <Button
                onClick={downloadReport}
                className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                Download PDF Report
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
